import React from "react";
import { Box, Button, Dialog, Divider, Typography } from "@mui/material";

import { getTwoDigitNo } from "utils";

/**
 * Fullscreen gate (mobile) — mobile equivalent of `dialogs/FullscreenPermissionDialog`.
 *
 * One component, two faces, exactly like desktop: `remainingTime` flips it from
 * the initial permission prompt to the mid-attempt "Attention!" warning. Copy is
 * identical to desktop's.
 *
 * NOTE: iOS Safari exposes no Fullscreen API for non-video elements, so on
 * iPhone `enableFullscreenMode()` is a no-op and this gate passes through
 * without real fullscreen. Android Chrome enforces it properly. That mirrors
 * how desktop already degrades — see ProctoringProvider.enableFullscreenMode.
 *
 * @param {boolean} open
 * @param {Function} onSuccess          grant/return-to fullscreen
 * @param {number|false} [remainingTime] ms left in the attempt; false = initial prompt
 */
export default function FullscreenDialog({
  open,
  onSuccess,
  remainingTime = false,
}) {
  const { min, sec } = React.useMemo(() => {
    let min;
    let sec;
    if (remainingTime) {
      min = getTwoDigitNo(Math.floor((remainingTime / 1000 / 60) % 60));
      sec = getTwoDigitNo(Math.floor((remainingTime / 1000) % 60));
    }
    return { min, sec };
  }, [remainingTime]);

  return (
    <Dialog
      open={open}
      fullWidth
      PaperProps={{ sx: { m: 3, borderRadius: 2 } }}
    >
      <Box
        sx={{
          px: 4,
          pt: 4,
          pb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography
          variant="h6-semiBold"
          color={remainingTime ? "danger.clr-700" : "neutral.clr-900"}
        >
          {remainingTime ? "Attention!" : "Full screen permission"}
        </Typography>
        {remainingTime ? (
          <Typography
            variant="h6-semiBold"
            color="danger.clr-700"
            sx={{ whiteSpace: "nowrap" }}
          >
            {min}:{sec} min
          </Typography>
        ) : null}
      </Box>

      <Divider sx={{ mx: 4 }} />

      <Box sx={{ p: 4 }}>
        <Typography
          variant="body01-regular"
          color="neutral.clr-900"
          sx={{ display: "block" }}
        >
          {remainingTime
            ? "To attempt this further, you must be in full screen mode. Continue your attempt before time runs out!"
            : "You can only attempt in full screen mode. Please switch to continue."}
        </Typography>

        <Button
          fullWidth
          variant="contained"
          onClick={onSuccess}
          sx={{ mt: 5 }}
        >
          {remainingTime ? "Return to full screen" : "Allow full screen mode"}
        </Button>
      </Box>
    </Dialog>
  );
}
